import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Skeleton from 'react-loading-skeleton'
import { Heading } from '../../../components/ui/Heading'
import trackEvents from '../../../constants/track-events'
import { trackEvent } from '../../../helpers/analytics'
import { useSelectionParams } from '../hooks/useSelectionParams'

export default function ProfileDropdown({
  className,
  selectedProfile,
  profileOptions,
  onProfileChange,
  isLoading
}) {
  const { t } = useTranslation()

  // Label aus den Optionen statt t() mit zusammengesetztem Key, sonst landet
  // bei fehlendem Profil 'discover.profile_options.null' als Missing Key.
  const selectedLabel = profileOptions.find(
    (profile) => profile.key === selectedProfile
  )?.label

  const { talents: selectedTalentIds, careers: selectedCareerIds } =
    useSelectionParams()

  const isProfileDisabled = (key) => {
    switch (key) {
      case 'MATCHINGPROFILE':
        return !(selectedTalentIds.length >= 1 && selectedCareerIds.length >= 1)
      case 'REQUIREMENTPROFILE':
        return !(selectedCareerIds.length >= 1)
      case 'TALENTPROFILE':
        return !(selectedTalentIds.length >= 1)
      case 'TEAMPROFILE':
        return !(selectedTalentIds.length >= 2 && selectedCareerIds.length >= 1)
      default:
        return false
    }
  }

  const getDisabledReason = (key) => {
    switch (key) {
      case 'MATCHINGPROFILE':
        if (selectedTalentIds.length < 1 && selectedCareerIds.length < 1) {
          return t('discover.profile_dropdown.disabled_reason.matching_both')
        } else if (selectedTalentIds.length < 1) {
          return t('discover.profile_dropdown.disabled_reason.matching_talent')
        } else if (selectedCareerIds.length < 1) {
          return t('discover.profile_dropdown.disabled_reason.matching_career')
        }
        return null
      case 'REQUIREMENTPROFILE':
        return selectedCareerIds.length < 1
          ? t('discover.profile_dropdown.disabled_reason.requirement')
          : null
      case 'TALENTPROFILE':
        return selectedTalentIds.length < 1
          ? t('discover.profile_dropdown.disabled_reason.talent')
          : null
      case 'TEAMPROFILE':
        if (selectedTalentIds.length < 2) {
          return t('discover.profile_dropdown.disabled_reason.team_talents')
        } else if (selectedCareerIds.length < 1) {
          return t('discover.profile_dropdown.disabled_reason.team_career')
        }
        return null
      default:
        return null
    }
  }

  return (
    <div
      className={clsx('flex flex-row items-center justify-start', className)}
    >
      <Menu as='div' className='relative'>
        <MenuButton className='flex items-center gap-2 focus:outline-none'>
          <Heading level={2} className={'text-darkBlue'}>
            {isLoading || !selectedLabel ? (
              <Skeleton width={150} />
            ) : (
              selectedLabel
            )}
          </Heading>
          {isLoading ? (
            <Skeleton width={32} height={32} />
          ) : (
            <div className='flex h-8 w-8 items-center justify-center rounded border border-lightGrey transition-colors hover:bg-gray-50'>
              <ChevronUpDownIcon className='size-4 text-buttonSecondaryBlue' />
            </div>
          )}
        </MenuButton>

        <MenuItems
          transition
          anchor='bottom start'
          className='data-closed:scale-95 data-closed:opacity-0 flex w-52 origin-top-left flex-col gap-y-1 rounded-lg border border-lightGrey bg-white p-1 text-sm shadow-lg transition duration-100 ease-out [--anchor-gap:4px] focus:outline-none'
        >
          {profileOptions.map((profile) => {
            const disabled = isProfileDisabled(profile.key)
            const disabledReason = disabled
              ? getDisabledReason(profile.key)
              : null
            return (
              <MenuItem key={profile.key}>
                <button
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return
                    trackEvent(trackEvents.DISCOVER_CLICK_PROFILE_OPTION, {
                      profile: profile.key
                    })
                    onProfileChange(profile.key)
                  }}
                  className={clsx(
                    'group flex w-full flex-col items-start rounded-md px-3 py-2 text-left transition-colors',
                    disabled
                      ? 'cursor-not-allowed bg-gray-50 text-gray-400'
                      : selectedProfile === profile.key
                        ? 'bg-blue-50 font-medium text-primaryBlue'
                        : 'text-darkBlue hover:bg-blue-50'
                  )}
                >
                  <span className='text-sm'>{profile.label}</span>
                  {disabledReason && (
                    <span className='mt-0.5 text-xs text-gray-500'>
                      {disabledReason}
                    </span>
                  )}
                </button>
              </MenuItem>
            )
          })}
        </MenuItems>
      </Menu>
    </div>
  )
}

ProfileDropdown.propTypes = {
  className: PropTypes.string,
  selectedProfile: PropTypes.string,
  profileOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onProfileChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
}
